import { useState, type ReactNode } from 'react';

import { TriangleAlertIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import ContextVariablesPanel from '@/components/atoms/ContextVariables/ContextVariables';
import { ExpandableAlert } from '@/components/atoms/ExpandableAlert/ExpandableAlert';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { type WebhookError } from '@/shared/types/tryIt';
import {
  WebhookType,
  type PreNbaWebhookTestResults,
  type WebhookTestResults as WebhookTestResultsType,
  type WebhookTestResultsResponse,
} from '@/shared/types/webhook';
import { BAD_EXECUTION_TIME, MODERATE_EXECUTION_TIME } from '@/util/constants';
import { toContextVariables } from '@/util/contextVariables';

interface WebhookTestResultsProps {
  testResults: WebhookTestResultsResponse | null;
  webhookType: WebhookType;
  onRefresh?: () => void;
  isCreateMode?: boolean;
}

const getStatusCode = (error?: WebhookError | null, hasResults?: boolean): number => {
  if (error) {
    return error.status_code ?? 0;
  }
  return hasResults ? 200 : 0;
};

const getStatusVariant = (statusCode: number) => {
  if (!statusCode) {
    return 'neutral' as const;
  }
  if (statusCode >= 200 && statusCode < 300) {
    return 'successful' as const;
  }
  if (statusCode >= 400 && statusCode < 500) {
    return 'warning' as const;
  }
  return 'destructive' as const;
};

const getErrorVariant = (statusCode: number | undefined) => {
  if (statusCode && statusCode >= 400 && statusCode < 500) {
    return 'warning' as const;
  }
  return 'destructive' as const;
};

const getExecutionTimeVariant = (duration: number) => {
  if (!duration) {
    return 'neutral' as const;
  }
  if (duration < MODERATE_EXECUTION_TIME) {
    return 'successful' as const;
  }
  if (duration < BAD_EXECUTION_TIME) {
    return 'warning' as const;
  }
  return 'destructive' as const;
};

interface ResultsSummaryProps {
  statusCode: number;
  duration: number;
  error?: WebhookError | null;
  rawResults: unknown;
  // Success body, rendered after the shared chrome. Receives showJson so each
  // view can hide its body while the raw JSON is shown.
  children?: (showJson: boolean) => ReactNode;
}

// Shared chrome for both result views: status/exec-time badges, error and
// performance alerts, and the raw-JSON toggle. The success body differs per
// view and comes in via children.
const ResultsSummary = ({
  statusCode,
  duration,
  error,
  rawResults,
  children,
}: ResultsSummaryProps) => {
  const [showJson, setShowJson] = useState(false);
  const { t } = useTranslation();

  const statusVariant = getStatusVariant(statusCode);
  const execTimeVariant = getExecutionTimeVariant(duration);
  const hasError = !!error;
  const hasPerformanceIssue = duration > MODERATE_EXECUTION_TIME;
  const isCriticalPerformance = duration > BAD_EXECUTION_TIME;

  return (
    <div className="flex flex-col gap-4">
      {/* Status badges */}
      <Card className="bg-background p-4 text-foreground shadow-none">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">{t('common.results')}</span>
          <div className="flex flex-wrap gap-2">
            <Badge type="secondary" flavour={statusVariant}>
              {t('webhook.test.status_code')}:{' '}
              <span className="font-semibold">{statusCode ? String(statusCode) : '-'}</span>
            </Badge>
            <Badge type="secondary" flavour={execTimeVariant}>
              {t('webhook.test.execution_time')}:{' '}
              <span className="font-semibold">{`${duration}ms`}</span>
            </Badge>
          </div>
        </div>
      </Card>

      {/* Error alert */}
      {hasError && (
        <ExpandableAlert
          variant={getErrorVariant(error?.status_code)}
          icon={<TriangleAlertIcon className="size-4" />}
          title={error?.code || t('common.error')}
          description={error?.message || t('webhook.test.request_failed')}
          descriptionClassName="break-all"
          expandedContent={
            <div className="space-y-2 text-sm">
              {error?.status_code != null && (
                <div>
                  <span className="font-medium">{t('common.status')}:</span> {error.status_code}
                </div>
              )}
              {error?.details && (
                <div className="wrap-break-word whitespace-pre-wrap">
                  <span className="font-medium">{t('team_details.details')}:</span> {error.details}
                </div>
              )}
            </div>
          }
          defaultExpanded={false}
        />
      )}

      {/* Performance alert */}
      {!hasError && hasPerformanceIssue && (
        <ExpandableAlert
          variant={isCriticalPerformance ? 'destructive' : 'warning'}
          title={
            isCriticalPerformance
              ? t('webhook.test.poor_performance')
              : t('webhook.test.moderate_performance')
          }
          description={`${t('webhook.test.execution_time')}: ${duration}ms`}
        />
      )}

      {/* JSON view toggle */}
      {!hasError && (
        <>
          <Card className="border border-border bg-background p-4 text-foreground">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('common.view_json')}</span>
              <Switch
                checked={showJson}
                onCheckedChange={setShowJson}
                aria-label={t('webhook.json', { defaultValue: 'View JSON' })}
              />
            </div>
          </Card>

          {showJson && (
            <pre className="max-h-96 overflow-auto rounded-md border border-border bg-background p-3 text-xs text-foreground">
              {JSON.stringify(rawResults, null, 2)}
            </pre>
          )}
        </>
      )}

      {children?.(showJson)}
    </div>
  );
};

interface DialogTestResultsProps {
  testResults: WebhookTestResultsType | null;
  onRefresh?: () => void;
  isCreateMode?: boolean;
}

const DialogWebhookTestResults = ({
  testResults,
  onRefresh,
  isCreateMode = false,
}: DialogTestResultsProps) => {
  const { t } = useTranslation();
  const statusCode = getStatusCode(testResults?.error, !!testResults);

  return (
    <ResultsSummary
      statusCode={statusCode}
      duration={testResults?.duration ?? 0}
      error={testResults?.error}
      rawResults={testResults}
    >
      {(showJson) =>
        !showJson && testResults?.context && Object.keys(testResults.context).length > 0 ? (
          <ContextVariablesPanel
            variables={toContextVariables(testResults.context)}
            onRefresh={onRefresh}
            tooltipText={t('webhook.test.upon_saving', {
              defaultValue: 'Variables will update after saving',
            })}
            hideRefreshButton={isCreateMode}
          />
        ) : null
      }
    </ResultsSummary>
  );
};

const PreNbaWebhookTestResultsView = ({
  testResults,
}: {
  testResults: PreNbaWebhookTestResults | null;
}) => {
  const { t } = useTranslation();
  const statusCode = getStatusCode(testResults?.error, !!testResults);
  const hasError = !!testResults?.error;

  return (
    <ResultsSummary
      statusCode={statusCode}
      duration={testResults?.duration_ms ?? 0}
      error={testResults?.error}
      rawResults={testResults}
    >
      {(showJson) =>
        !hasError && !showJson ? (
          <Card className="bg-background p-4 text-foreground shadow-none">
            <span className="text-sm font-medium">{t('webhook.test.external_state')}</span>
            <pre className="mt-2 max-h-96 overflow-auto rounded-md border border-border bg-background p-3 text-xs whitespace-pre-wrap text-foreground">
              {testResults?.external_state || t('webhook.test.external_state_empty')}
            </pre>
          </Card>
        ) : null
      }
    </ResultsSummary>
  );
};

export const WebhookTestResults = ({
  testResults,
  webhookType,
  onRefresh,
  isCreateMode = false,
}: WebhookTestResultsProps) => {
  if (webhookType === WebhookType.PRE_NBA) {
    return (
      <PreNbaWebhookTestResultsView testResults={testResults as PreNbaWebhookTestResults | null} />
    );
  }

  return (
    <DialogWebhookTestResults
      testResults={testResults as WebhookTestResultsType | null}
      onRefresh={onRefresh}
      isCreateMode={isCreateMode}
    />
  );
};
